import React, { useMemo, useState } from 'react';

import classnames from 'classnames';

import MenuItem from '@mui/material/MenuItem';

import SelectDropdown from 'components/uiLibrary/Inputs/SelectDropdown';
import Typography from 'components/uiLibrary/Typography';

import { COMPONENTS } from 'components/Globals/Analytics/constants';
import EntityName from 'components/Globals/EntityName';
import { SectionHeading } from 'components/Globals/SectionBlock';

import { TP } from 'constants/index';

import { getProgramLabel } from 'utils/common';
import { getProductionCredits, filterDigitalDates } from 'utils/productions';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import { SECTION_KEYS } from '../constants';
import CreditInfoItem from '../CreditInfoItem/CreditInfoItem';
import SectionHeader from '../SectionHeader';
import { getSectionsVisibility, splitOrMergeContributions } from '../utils';

import classes from './CastAndCrew.module.scss';

const ALL_CAST_ID = '_all_cast_and_crew';

const RenderEntityName = ({ option, trackingData }) => (
  <EntityName
    entity={option}
    entityType={ENTITY_TYPES.WORK}
    typographyProps={{
      work: {
        weight: 'bold',
      },
      translation: {
        size: 14,
      },
      composer: {
        size: 14,
      },
    }}
    withComposers
    onlyShortName
    trackingData={option?.value === ALL_CAST_ID ? undefined : trackingData}
  />
);

const ListItems = ({
  item,
  isArchived,
  physicalPerformancesCount,
  productionWorksCount,
  quickViewContextId,
  hasMultipleWorks,
  selectedPerformanceId,
  trackingData,
  t,
  handleRoleSelect,
  onCloseQuickView,
}) => {
  if (!item.items?.length) {
    return (
      <Typography variant="p" size={14} color="secondary" italic className={classes.itemsNotPresent}>
        {t(`${TP}.FN_SECTION_NOT_AVAILABLE_FOR_PROGRAM`, { section: item.title })}
      </Typography>
    );
  }

  return splitOrMergeContributions(item.items)?.map((crewMember, index) => (
    <CreditInfoItem
      key={`${crewMember.title}_${index}`}
      {...crewMember}
      title={t(crewMember.title)}
      isArchived={isArchived}
      showDetailedView
      showDates={item.showDates || crewMember.showDates}
      showWorks={hasMultipleWorks}
      physicalPerformancesCount={physicalPerformancesCount}
      productionWorksCount={productionWorksCount}
      quickViewContextId={quickViewContextId}
      selectedPerformanceId={selectedPerformanceId}
      trackingData={trackingData}
      t={t}
      handleRoleSelect={handleRoleSelect}
      workRoleId={crewMember.workRoleId}
      professionId={crewMember.professionId}
      onCloseQuickView={onCloseQuickView}
    />
  ));
};

const SectionRenderer = ({
  isDesktopStubPage,
  item,
  isArchived,
  physicalPerformancesCount,
  productionWorksCount,
  quickViewContextId,
  hasMultipleWorks,
  selectedPerformanceId,
  t,
  trackingData,
  handleRoleSelect,
  onCloseQuickView,
}) => (
  <>
    {isDesktopStubPage ? (
      <SectionHeading title={item.title} className={classes.crewListHeader} />
    ) : (
      <SectionHeader title={item.title} className={classes.crewListHeader} />
    )}
    <ListItems
      item={item}
      isArchived={isArchived}
      physicalPerformancesCount={physicalPerformancesCount}
      productionWorksCount={productionWorksCount}
      quickViewContextId={quickViewContextId}
      hasMultipleWorks={hasMultipleWorks}
      selectedPerformanceId={selectedPerformanceId}
      t={t}
      trackingData={trackingData}
      handleRoleSelect={handleRoleSelect}
      onCloseQuickView={onCloseQuickView}
    />
  </>
);

const CastAndCrew = ({
  entity: production,
  quickViewContextId,
  selectedPerformanceId,
  trackingData,
  isDesktopStubPage,
  handleRoleSelect,
  onCloseQuickView,
}) => {
  const { t } = useTranslation('NS_ENTITY_STUB_PAGE');

  const [workId, setWorkId] = useState(ALL_CAST_ID);
  const performanceDate = production?.performances?.find(performance => performance.id === selectedPerformanceId)
    ?.startDate;
  // TODO: Try passing entityId: quickViewContextId, to getProductionCredits(), and use isCurrentEntity to highlight
  const credits = useMemo(
    () =>
      getProductionCredits(production, {
        workId: workId === ALL_CAST_ID ? undefined : workId,
        performanceDate,
        hideNonContributors: false,
      }),
    [production, workId, performanceDate],
  );
  const [sectionsToShow] = useState(getSectionsVisibility(credits));

  const productionWorks = production?.productionWorks;
  const isArchived = production?.isArchived;
  const workOptions = useMemo(() => {
    if (productionWorks?.length <= 1 && !production?.isProgramNotAnnounced) {
      return [];
    }

    const options = [
      {
        label: t(`${TP}.FN_ALL_CAST_AND_CREW`),
        value: ALL_CAST_ID,
        original_name: t(`${TP}.FN_ALL_CAST_AND_CREW`),
      },
      ...(production?.isProgramNotAnnounced
        ? [
            {
              value: -1,
              label: t(getProgramLabel(production, production?.productionWorks?.length)),
              name: t(getProgramLabel(production, production?.productionWorks?.length)),
            },
          ]
        : []),
      ...(production?.productionWorks?.map(work => ({
        label: work?.work?.original_name,
        value: work?.work?.id,
        original_name: work?.work?.original_name,
        name: work?.work?.name,
        creators: work?.work?.creators,
      })) || []),
    ];

    return options;
  }, [production]);

  const { cast, producers, directors, conductors, crew, instrumentalists, ensembles, coProducers } = credits;

  const ensembleOption = {
    title: t(`${TP}.FN_ENSEMBLE`),
    items: ensembles,
    key: SECTION_KEYS.ENSEMBLES,
    showDates: true,
    enabled: sectionsToShow[SECTION_KEYS.ENSEMBLES] || isDesktopStubPage,
  };
  const producerOption = {
    title: t(`${TP}.FN_PRODUCER`),
    items: producers?.data.length > 0 ? [producers] : [],
    key: SECTION_KEYS.PRODUCERS,
    showDates: true,
    enabled: sectionsToShow[SECTION_KEYS.PRODUCERS] || isDesktopStubPage,
  };
  const coProducerOption = {
    title: t(`${TP}.FN_CO_PRODUCER`),
    items: coProducers?.data.length > 0 ? [coProducers] : [],
    key: SECTION_KEYS.CO_PRODUCERS,
    showDates: true,
    enabled: sectionsToShow[SECTION_KEYS.CO_PRODUCERS] || isDesktopStubPage,
  };

  const castAndCrewList = [
    {
      title: t(`${TP}.FN_CAST`),
      items: cast,
      key: SECTION_KEYS.CAST,
      showDates: true,
      enabled: sectionsToShow[SECTION_KEYS.CAST] || isDesktopStubPage,
    },
    {
      title: t(`${TP}.FN_CREW_TITLE`),
      items: [
        directors && { ...directors, showDates: true },
        conductors && { ...conductors, showDates: true },
        ...(crew || []),
      ].filter(Boolean).sort((a, b) => {
        // Sort crew sections by profession order (ascending)
        const getMinOrder = (section) => {
          if (!section?.data?.length) return 99999;
          return Math.min(...section.data.map(person => person?.profession?.order ?? 99999));
        };
        
        const orderA = getMinOrder(a);
        const orderB = getMinOrder(b);
        return orderA - orderB;
      }),
      key: SECTION_KEYS.CREW,
      enabled: sectionsToShow[SECTION_KEYS.CREW] || isDesktopStubPage,
    },
    {
      title: t(`${TP}.FN_INSTRUMENTATION`),
      items: instrumentalists,
      key: SECTION_KEYS.INSTRUMENTALISTS,
      showDates: true,
      enabled: sectionsToShow[SECTION_KEYS.INSTRUMENTALISTS] || isDesktopStubPage,
    },
    ...(isDesktopStubPage
      ? [{ sections: [producerOption, ensembleOption, coProducerOption], enabled: true }]
      : [ensembleOption, coProducerOption]),
  ].filter(item => item.enabled);

  const hasMultipleWorks = workOptions.length > 1;
  const physicalPerformancesCount = filterDigitalDates(production)?.length;

  const multipleMusicalWorkDropdownTrackingData = {
    ...trackingData,
    component: COMPONENTS.MULTIPLE_MUSICAL_WORK_DROPDOWN,
  };

  return (
    <div
      className={classnames(hasMultipleWorks ? classes.multipleWorks : classes.singleWork, {
        [classes.isDesktopStubPage]: isDesktopStubPage,
      })}
      data-sectionid={SECTION_KEYS.CREDITS}
    >
      {hasMultipleWorks && (
        <SelectDropdown
          name={t(`${TP}.FN_PROGRAMS`)}
          options={workOptions}
          label={t(`${TP}.FN_PROGRAMS`)}
          value={workId}
          onChange={event => setWorkId(event.target?.value)}
          renderValue={value => {
            const entity = workOptions.find(option => option.value === value);
            return (
              entity && <RenderEntityName option={entity} trackingData={multipleMusicalWorkDropdownTrackingData} />
            );
          }}
          className={classes.workSelect}
          size="small"
          styles={{
            formControl: classes.workSelectForm,
          }}
          trackingData={multipleMusicalWorkDropdownTrackingData}
        >
          {({ options }) =>
            options.map(option => (
              <MenuItem value={option.value} classes={{ root: classes.workSelectItem }}>
                <RenderEntityName option={option} trackingData={multipleMusicalWorkDropdownTrackingData} />
              </MenuItem>
            ))
          }
        </SelectDropdown>
      )}

      <div className={classnames(classes.crewListSectionsContainer)}>
        {castAndCrewList.map(item => {
          const sectionProps = {
            isDesktopStubPage,
            isArchived,
            physicalPerformancesCount,
            productionWorksCount: productionWorks?.length,
            quickViewContextId,
            hasMultipleWorks,
            selectedPerformanceId,
            t,
            trackingData,
            handleRoleSelect,
            onCloseQuickView,
          };
          return (
            <div className={classes.crewListSection} key={item.key} data-sectionid={item.key}>
              {item.sections ? (
                item.sections.map(section => (
                  <div key={section.key}>
                    <SectionRenderer item={section} {...sectionProps} />
                  </div>
                ))
              ) : (
                <SectionRenderer item={item} {...sectionProps} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CastAndCrew;
